import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@vinoga/db";
import {
  ArrowLeft,
  Package,
  CreditCard,
  Clock,
  MapPin,
  User,
  Snowflake,
  PartyPopper,
  ShieldCheck,
  RotateCcw,
  Save,
  FileText,
  Receipt,
  Mail,
  Phone,
  Truck,
  X,
  Inbox,
  Send,
  CheckCircle2,
  Eye,
  MousePointerClick,
  XCircle,
  AlertCircle,
  MessageCircle,
  Activity,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { mediaSrc } from "@/lib/media";
import { api, ApiError, ADMIN_TOKEN_KEY } from "@/lib/api";
import { generateInvoicePdf } from "@/lib/invoice-pdf";
import { earliestPickupHandoverAt } from "@/lib/pickup-handover";

const sectionClass = "bg-card border border-border rounded-lg p-5 space-y-4";

type Order = {
  id: string;
  order_number: string;
  status: "pending" | "paid" | "ready" | "fulfilled" | "cancelled" | "refunded";
  payment_status: "pending" | "paid" | "partially_refunded" | "refunded" | "failed";
  customer_email: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_phone: string | null;
  subtotal: string;
  shipping_cost: string;
  deposit_total: string;
  coupon_discount: string;
  total_amount: string;
  fulfilment_type: "pickup" | "courier";
  pickup_location_id: string | null;
  fulfilment_store_id: string | null;
  courier_provider: string | null;
  venipak_status: string | null;
  venipak_pack_numbers: string[] | null;
  venipak_manifest_code: string | null;
  venipak_dispatched_at: string | null;
  payment_method: string | null;
  payment_method_detail: string | null;
  shipping_address: Record<string, unknown> | null;
  pickup_point_terminal: Record<string, unknown> | null;
  receive_chilled: boolean;
  for_banquet: boolean;
  earliest_delivery_at: string | null;
  age_verification_id: string | null;
  is_b2b_invoice: boolean;
  billing_details: {
    buyerType?: "private" | "business";
    companyName?: string;
    regNumber?: string;
    vatNumber?: string;
    legalAddress?: string;
  } | null;
  gift_wrap: Array<{ productId: string; sku: string | null; name: string; qty: number }> | null;
  gift_wrap_cost: string | null;
  customer_note: string | null;
  internal_note: string | null;
  archived_at: string | null;
  tracking_number: string | null;
  created_at: string;
};

type OrderCommunication = {
  id: string;
  channel: "email" | "sms";
  provider: string | null;
  template: string;
  recipient: string;
  subject: string | null;
  status: string;
  created_at: string;
};

export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const auth = useAuth();
  const t = useT();

  const [order, setOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  // Latest Moneo pushInvoice event for this order — drives the "Moneo"
  // row in the payment card so staff can see whether the invoice reached
  // the ERP without needing Moneo access.
  const [moneoEvent, setMoneoEvent] = useState<{
    status: "success" | "error";
    error: string | null;
    created_at: string;
  } | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [comms, setComms] = useState<OrderCommunication[]>([]);
  // product_id → primary image URL. Fetched lazily once we have items so
  // we can render thumbnails in the Products table without a per-row N+1.
  const [productImages, setProductImages] = useState<Record<string, string>>({});
  // Click a row thumbnail → show full image in a fixed overlay (mirrors
  // miluliem's OrderDetail behaviour). null = closed.
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Stores available for fulfilment reassignment. Loaded once when the
  // page mounts so the dropdown opens instantly.
  const [stores, setStores] = useState<Array<{ id: string; name: string }>>([]);
  const [dispatching, setDispatching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const [internalNote, setInternalNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [originalNote, setOriginalNote] = useState("");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  const adminUserId = auth.status === "authenticated" ? auth.user.id : null;

  const refresh = async () => {
    if (!id) return;
    const [o, h] = await Promise.all([
      supabase.from("orders").select("*").eq("id", id).single(),
      supabase
        .from("order_status_history")
        .select("*")
        .eq("order_id", id)
        .order("created_at", { ascending: false }),
    ]);
    if (o.data) {
      setOrder(o.data as Order);
      const note = (o.data as any).internal_note ?? "";
      setInternalNote(note);
      setOriginalNote(note);
    }
    setHistory(h.data ?? []);
  };

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [o, oi, p, h, me] = await Promise.all([
        supabase.from("orders").select("*").eq("id", id).single(),
        supabase
          .from("order_items")
          .select("*")
          .eq("order_id", id)
          .order("created_at"),
        supabase.from("payments").select("*").eq("order_id", id).order("created_at"),
        supabase
          .from("order_status_history")
          .select("*")
          .eq("order_id", id)
          .order("created_at", { ascending: false }),
        supabase
          .from("integration_events")
          .select("*")
          .eq("integration", "moneo")
          .eq("operation", "pushInvoice")
          .eq("reference_id", id)
          .order("created_at", { ascending: false })
          .limit(1),
      ]);
      if (cancelled) return;
      if (o.error || !o.data) {
        toast({
          title: t("common.notFound", "Not found"),
          description: o.error?.message ?? t("orderDetail.notFound", "Order not found"),
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      setOrder(o.data as Order);
      setItems(oi.data ?? []);
      setPayments(p.data ?? []);
      setHistory(h.data ?? []);
      setMoneoEvent((me.data?.[0] as typeof moneoEvent) ?? null);
      const note = (o.data as any).internal_note ?? "";
      setInternalNote(note);
      setOriginalNote(note);
      setLoading(false);

      // Communication log — best-effort. Empty list on error so the
      // panel just shows "no messages yet" instead of breaking the page.
      api<{ communications: OrderCommunication[] }>(
        `/api/v1/admin/communications/by-order/${id}`,
      )
        .then((res) => {
          if (!cancelled) setComms(res.communications ?? []);
        })
        .catch(() => {
          if (!cancelled) setComms([]);
        });

      // Pull primary product images so the Products table can render
      // thumbnails. Skipped for items whose product_id was nulled out
      // (the line still renders without a thumb).
      const productIds = Array.from(
        new Set(
          ((oi.data ?? []) as Array<{ product_id: string | null }>)
            .map((it) => it.product_id)
            .filter((v): v is string => typeof v === "string"),
        ),
      );
      if (productIds.length > 0) {
        const imgRes = await supabase
          .from("product_images")
          .select("product_id, url, position")
          .in("product_id", productIds);
        const byProduct: Record<string, { url: string; position: number }> = {};
        for (const row of (imgRes.data ?? []) as Array<{
          product_id: string;
          url: string;
          position: number;
        }>) {
          const prev = byProduct[row.product_id];
          if (!prev || row.position < prev.position) {
            byProduct[row.product_id] = { url: row.url, position: row.position };
          }
        }
        if (!cancelled) {
          setProductImages(
            Object.fromEntries(
              Object.entries(byProduct).map(([k, v]) => [k, v.url]),
            ),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t, toast]);

  // Stores list — used by the fulfilment-store dropdown on the
  // Delivery card. Only "shop" rows are eligible; warehouses are
  // hidden so a typo in the data doesn't break dispatch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("inventory_locations")
        .select("id, name, type, active")
        .eq("type", "shop")
        .eq("active", true)
        .order("position");
      if (cancelled) return;
      setStores(((data ?? []) as Array<{ id: string; name: string }>).map((r) => ({
        id: r.id,
        name: r.name,
      })));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateOrder = async (patch: Record<string, unknown>, historyNote?: string) => {
    if (!order) return;
    setWorking(true);
    const { error } = await supabase.from("orders").update(patch).eq("id", order.id);
    if (error) {
      toast({
        title: t("orderDetail.updateFailed", "Update failed"),
        description: error.message,
        variant: "destructive",
      });
      setWorking(false);
      return;
    }
    // Append a history row attributed to the current admin so the audit trail
    // shows who made the change (the trigger writes a parallel 'system' row;
    // we tolerate that for now).
    if (adminUserId && (patch.status || patch.payment_status)) {
      await supabase.from("order_status_history").insert({
        order_id: order.id,
        field: patch.status ? "status" : "payment_status",
        from_value: patch.status ? order.status : order.payment_status,
        to_value: (patch.status ?? patch.payment_status) as string,
        changed_by: adminUserId,
        changed_by_kind: "admin",
        note: historyNote ?? null,
      });
    }
    await refresh();
    setWorking(false);
  };

  const markAsPaid = () =>
    updateOrder({
      status: "paid",
      payment_status: "paid",
      paid_at: new Date().toISOString(),
    });

  const markAsReady = () => updateOrder({ status: "ready" });

  const markAsFulfilled = () =>
    updateOrder({
      status: "fulfilled",
      fulfilled_at: new Date().toISOString(),
    });

  const cancelOrder = async () => {
    setCancelOpen(false);
    await updateOrder({
      status: "cancelled",
      cancelled_at: new Date().toISOString(),
    });
  };

  // Hide an order from the lists without deleting it. Used for the
  // testing-period cleanup and any junk order staff want out of the way;
  // archived orders reappear via "Rādīt arhivētos" on the orders page.
  const toggleArchive = async () => {
    if (!order) return;
    await updateOrder({
      archived_at: order.archived_at ? null : new Date().toISOString(),
    });
  };

  const refundOrder = async () => {
    setRefundOpen(false);
    await updateOrder({
      status: "refunded",
      payment_status: "refunded",
    });
  };

  // Reassign which store fulfils this order. The dispatch endpoint
  // reads fulfilment_store_id when it builds the Venipak sender block,
  // so this also re-points the return address on the next label.
  const reassignStore = (storeId: string) => {
    if (!order || storeId === order.fulfilment_store_id) return;
    void updateOrder({ fulfilment_store_id: storeId });
  };

  const dispatchToCourier = async () => {
    if (!order) return;
    setDispatching(true);
    try {
      const result = await api<{
        status: string;
        packNumbers?: string[];
        manifestCode?: string;
        error?: string;
      }>(`/api/v1/admin/orders/${order.id}/dispatch`, { method: "POST" });
      toast({
        title:
          result.status === "already_sent"
            ? t("orderDetail.dispatchAlready", "Already dispatched")
            : t("orderDetail.dispatchOk", "Sent to courier"),
        description: result.packNumbers?.join(", "),
      });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      toast({
        title: t("orderDetail.dispatchFailed", "Dispatch failed"),
        description: msg,
        variant: "destructive",
      });
    } finally {
      setDispatching(false);
    }
  };

  // PDF can't be opened with a custom Authorization header via window.open,
  // so we fetch the blob ourselves + open an object URL. Same pattern would
  // work for any future provider's label endpoint.
  const downloadLabel = async () => {
    if (!order) return;
    try {
      const token = localStorage.getItem(ADMIN_TOKEN_KEY);
      const res = await fetch(
        `${import.meta.env.VITE_API_URL ?? ""}/api/v1/admin/orders/${order.id}/label.pdf`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {
          /* not json */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Revoke after a beat — the browser keeps the open tab alive
      // independently, so we can free the URL once the navigation has had
      // time to start.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      toast({
        title: t("orderDetail.labelFailed", "Label download failed"),
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  const saveNote = async () => {
    if (!order || internalNote === originalNote) return;
    setNoteSaving(true);
    const { error } = await supabase
      .from("orders")
      .update({ internal_note: internalNote || null })
      .eq("id", order.id);
    if (error) {
      toast({
        title: t("orderDetail.noteSaveFailed", "Failed to save note"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      setOriginalNote(internalNote);
      toast({ title: t("orderDetail.noteSaved", "Note saved") });
    }
    setNoteSaving(false);
  };

  if (loading || !order) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {t("common.loading", "Loading...")}
      </div>
    );
  }

  const customerName =
    [order.customer_first_name, order.customer_last_name].filter(Boolean).join(" ") || "—";

  const canMarkPaid = order.status === "pending";
  const canMarkReady = order.status === "paid";
  const canMarkFulfilled = order.status === "ready";
  const canCancel = !["cancelled", "refunded", "fulfilled"].includes(order.status);
  const canRefund =
    order.payment_status === "paid" && !["cancelled", "refunded"].includes(order.status);

  return (
    <div className="space-y-6 pb-20">
      {/* Header — miluliem-style with Print Order / Print Invoice / Edit
        buttons + status badge. Action bar removed; order-status changes
        now live inside the Notes card, payment-status changes inside the
        Payment card (matches miluliem). */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/orders")}
          className="p-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="font-display text-xl font-bold text-foreground">
            {order.order_number}
          </h1>
          <p className="font-body text-xs text-muted-foreground">
            {t("orderDetail.created", "Created")}{" "}
            {new Date(order.created_at).toLocaleString("en-GB")}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <FileText size={14} className="mr-1" />
            {t("orderDetail.printOrder", "Print Order")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateInvoicePdf(order, items)}
          >
            <Receipt size={14} className="mr-1" />
            {t("orderDetail.printInvoice", "Print Invoice")}
          </Button>
          <StatusBadge status={order.status} t={t} />
          <PaymentBadge status={order.payment_status} t={t} />
        </div>
      </div>

      {/* Miluliem-style 2/3 + 1/3 split. Left: Items+Pricing → Notes →
        Timeline. Right: Payments → Customer → Delivery. Refund history +
        Communications log sections from miluliem are omitted intentionally —
        Vīnoga API doesn't track that data yet, so empty stubs would lie. */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* LEFT — items+pricing → notes → timeline */}
        <div className="space-y-6 xl:col-span-2">
          <section className="space-y-3 rounded-lg border border-border bg-card p-5">
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Package size={16} className="text-muted-foreground" />{" "}
              {t("orderDetail.products", "Products")}
            </h2>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[56px]"></TableHead>
                  <TableHead className="w-[80px] text-[11px] font-bold uppercase tracking-wider">
                    {t("orderDetail.colSku", "SKU")}
                  </TableHead>
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider">
                    {t("orderDetail.colProduct", "Product")}
                  </TableHead>
                  <TableHead className="text-center text-[11px] font-bold uppercase tracking-wider">
                    {t("orderDetail.colQty", "Qty")}
                  </TableHead>
                  <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                    {t("orderDetail.colUnitPrice", "Unit price")}
                  </TableHead>
                  <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                    {t("orderDetail.colTotal", "Total")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => {
                  const rawImg = it.product_id
                    ? productImages[it.product_id]
                    : undefined;
                  // Legacy paths from the original Vīnoga import are
                  // stored as bare R2 keys (e.g. "vinoga/uploads/…webp"
                  // with spaces and unicode). `mediaSrc` prepends the
                  // public bucket origin + URL-encodes each segment.
                  const img = mediaSrc(rawImg ?? null);
                  return (
                    <TableRow key={it.id}>
                      <TableCell>
                        {img ? (
                          <button
                            type="button"
                            onClick={() => setLightboxUrl(img)}
                            className="grid h-12 w-12 cursor-pointer place-items-center overflow-hidden rounded border border-border bg-muted/30 transition-opacity hover:opacity-80"
                            aria-label={t("orderDetail.openImage", "Open image")}
                          >
                            <img
                              src={img}
                              alt={it.name}
                              className="h-full w-full object-contain p-1"
                              loading="lazy"
                            />
                          </button>
                        ) : (
                          <div className="grid h-12 w-12 place-items-center overflow-hidden rounded border border-border bg-muted/30">
                            <Package
                              size={18}
                              className="text-muted-foreground/40"
                            />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {it.sku}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{it.name}</div>
                        {it.requires_deposit && (
                          <div className="font-body text-[11px] text-muted-foreground">
                            {t("orderDetail.depositLine", "incl. deposit")} €
                            {Number(it.deposit_unit).toFixed(2)} ×{it.qty}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground">
                        ×{it.qty}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        €{Number(it.unit_price).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        €{Number(it.line_total).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="space-y-1 border-t border-border pt-3 text-sm">
              <Row
                label={t("orderDetail.subtotal", "Subtotal")}
                value={`€${num(order.subtotal)}`}
              />
              {Number(order.coupon_discount) > 0 && (
                <Row
                  label={t("orderDetail.discount", "Discount")}
                  value={`-€${num(order.coupon_discount)}`}
                  muted
                />
              )}
              {Number(order.deposit_total) > 0 && (
                <Row
                  label={t("orderDetail.deposit", "Bottle deposit")}
                  value={`€${num(order.deposit_total)}`}
                />
              )}
              {Number(order.gift_wrap_cost ?? 0) > 0 && (
                <Row
                  label={t("orderDetail.giftWrap", "Gift wrapping")}
                  value={`€${num(order.gift_wrap_cost ?? "0")}`}
                />
              )}
              <Row
                label={t("orderDetail.shipping", "Shipping")}
                value={`€${num(order.shipping_cost)}`}
              />
              {/* Which items to wrap (per-item selection from checkout). */}
              {order.gift_wrap && order.gift_wrap.length > 0 && (
                <div className="pt-2">
                  <div className="font-body text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t("orderDetail.giftWrapItems", "Gift-wrap these items")}
                  </div>
                  {order.gift_wrap.map((g) => (
                    <div key={g.productId} className="text-muted-foreground">
                      {g.qty} × {g.name}
                    </div>
                  ))}
                </div>
              )}
              <Row
                label={t("orderDetail.total", "Total")}
                value={`€${num(order.total_amount)}`}
                bold
              />
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="font-display text-sm font-bold text-foreground">
              {t("orderDetail.notes", "Notes")}
            </h2>
            {order.customer_note ? (
              <div>
                <div className="font-body text-xs uppercase tracking-wider text-muted-foreground">
                  {t("orderDetail.customerNote", "From customer")}
                </div>
                <p className="text-sm">{order.customer_note}</p>
              </div>
            ) : null}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="font-body text-xs uppercase tracking-wider text-muted-foreground">
                  {t("orderDetail.internalNote", "Internal")}
                </div>
                {internalNote !== originalNote && (
                  <Button
                    size="sm"
                    onClick={saveNote}
                    disabled={noteSaving}
                    className="h-7 px-2 text-xs"
                  >
                    <Save size={12} />
                    {noteSaving
                      ? t("common.saving", "Saving…")
                      : t("common.save", "Save")}
                  </Button>
                )}
              </div>
              <textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                rows={4}
                placeholder={t(
                  "orderDetail.internalNotePlaceholder",
                  "Internal-only note (not visible to customer)"
                )}
                className="w-full resize-y rounded-md border border-border bg-card px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground"
              />
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Clock size={16} className="text-muted-foreground" />{" "}
              {t("orderDetail.history", "Timeline")}
            </h2>
            {history.length === 0 ? (
              <p className="font-body text-xs text-muted-foreground">
                {t("orderDetail.historyEmpty", "No status changes yet.")}
              </p>
            ) : (
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.id} className="flex items-start gap-3 text-sm">
                    <div className="mt-1.5 size-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                    <div className="flex-1">
                      <div className="font-medium">
                        {h.field === "status"
                          ? t("orderDetail.statusChange", "Status")
                          : t("orderDetail.paymentChange", "Payment")}
                        : {h.from_value ?? "—"} → {h.to_value}
                      </div>
                      <div className="font-body text-[11px] text-muted-foreground">
                        {new Date(h.created_at).toLocaleString()} ·{" "}
                        {h.changed_by_kind ?? "system"}
                      </div>
                      {h.note && (
                        <div className="mt-1 font-body text-xs italic text-muted-foreground">
                          {h.note}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Communication History — emails sent via Brevo for this
            order. Fed by the order_communications table; the row's
            status is kept current by /webhooks/brevo. */}
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Inbox size={16} className="text-muted-foreground" />{" "}
              {t("orderDetail.communicationsTitle", "Communication History")}
            </h2>
            {comms.length === 0 ? (
              <p className="font-body text-xs text-muted-foreground">
                {t(
                  "orderDetail.communicationsEmpty",
                  "No messages sent yet.",
                )}
              </p>
            ) : (
              <div className="max-h-72 space-y-2 overflow-y-auto">
                {comms.map((c) => (
                  <CommunicationRow key={c.id} comm={c} />
                ))}
              </div>
            )}
          </section>
        </div>

        {/* RIGHT — order status → payments → customer → delivery */}
        <div className="space-y-6">
          {/* Order status — split out of Notes so it sits at the top of the
            sidebar as the primary action target. History + customer/internal
            notes still live in the left column. */}
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Activity size={16} className="text-muted-foreground" />{" "}
              {t("orderDetail.orderStatus", "Order Status")}
            </h2>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {t("orderDetail.currentStatus", "Current")}
              </span>
              <StatusBadge status={order.status} t={t} />
            </div>
            <Select
              value={order.status}
              onValueChange={(value) => {
                if (value === order.status) return;
                if (value === "cancelled") {
                  setCancelOpen(true);
                  return;
                }
                if (value === "refunded") {
                  setRefundOpen(true);
                  return;
                }
                if (value === "paid") return void markAsPaid();
                if (value === "ready") return void markAsReady();
                if (value === "fulfilled") return void markAsFulfilled();
                void updateOrder({ status: value });
              }}
              disabled={working}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["pending", "paid", "ready", "fulfilled", "cancelled", "refunded"].map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Archive = hide from the order lists without deleting. */}
            <button
              onClick={() => void toggleArchive()}
              disabled={working}
              className="w-full rounded-lg border border-border px-3 py-2 font-body text-sm text-muted-foreground hover:bg-secondary disabled:opacity-50"
            >
              {order.archived_at
                ? t("orderDetail.unarchive", "Atjaunot no arhīva")
                : t("orderDetail.archive", "Arhivēt (paslēpt no saraksta)")}
            </button>
            {order.archived_at && (
              <p className="font-body text-xs text-muted-foreground">
                {t("orderDetail.archivedNote", "Šis pasūtījums ir arhivēts un sarakstā neparādās.")}
              </p>
            )}
          </section>

          {/* Payment card — Status / Method / Captured rows + Refund button.
            Payment status is read-only here; it flips automatically when the
            order moves to paid/refunded. */}
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <CreditCard size={16} className="text-muted-foreground" />{" "}
              {t("orderDetail.payment", "Payment")}
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  {t("orderDetail.paymentStatusLabel", "Status")}
                </span>
                <PaymentBadge status={order.payment_status} t={t} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  {t("orderDetail.paymentMethodLabel", "Method")}
                </span>
                <span className="text-foreground">
                  {order.payment_method === "bank_transfer"
                    ? t("orderDetail.bankTransfer", "Pēcapmaksa (pārskaitījums)")
                    : (order.payment_method_detail ??
                      (order.payment_method
                        ? order.payment_method.charAt(0).toUpperCase() +
                          order.payment_method.slice(1)
                        : "—"))}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  {t("orderDetail.captured", "Captured")}
                </span>
                <span className="font-medium text-foreground">
                  {order.payment_status === "paid" ||
                  order.payment_status === "partially_refunded" ||
                  order.payment_status === "refunded"
                    ? `€${num(order.total_amount)}`
                    : "—"}
                </span>
              </div>
              {/* Moneo push state: success → sent (+date), error → the push
                  keeps retrying hourly via the health catch-up, so show it as
                  pending with the last error underneath. No event yet → "—"
                  (either the toggle is off or the order predates the push). */}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Moneo</span>
                {moneoEvent?.status === "success" ? (
                  <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                    {t("orderDetail.moneoSent", "Nosūtīts")} ·{" "}
                    {new Date(moneoEvent.created_at).toLocaleDateString("lv-LV")}
                  </span>
                ) : moneoEvent?.status === "error" ? (
                  <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                    {t("orderDetail.moneoRetrying", "Gaida (mēģina atkārtoti)")}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
              {moneoEvent?.status === "error" && moneoEvent.error && (
                <p className="break-all text-[11px] leading-snug text-muted-foreground">
                  {moneoEvent.error.slice(0, 160)}
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={!canRefund || working}
              onClick={() => setRefundOpen(true)}
            >
              <RotateCcw size={14} className="mr-1" />
              {t("orderDetail.refund", "Refund")}
            </Button>
            {payments.length > 0 && (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="font-body text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t("orderDetail.paymentAttempts", "Attempts")}
                </div>
                {payments.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between text-xs text-muted-foreground"
                  >
                    <span>
                      {p.provider} · {p.provider_method ?? "—"}
                    </span>
                    <span>€{num(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <User size={16} className="text-muted-foreground" />{" "}
              {t("orderDetail.customer", "Customer")}
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <User size={14} className="text-muted-foreground" />
                <span className="text-foreground">{customerName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-muted-foreground" />
                <span className="break-all text-foreground">
                  {order.customer_email}
                </span>
              </div>
              {order.customer_phone && (
                <div className="flex items-center gap-2">
                  <Phone size={14} className="text-muted-foreground" />
                  <span className="text-foreground">
                    {order.customer_phone}
                  </span>
                </div>
              )}
              {order.is_b2b_invoice && (
                <Badge variant="info">
                  {t("orderDetail.b2bInvoice", "B2B invoice")}
                </Badge>
              )}
              {/* Full legal requisites for "Juridiska persona" orders —
                snapshot taken at checkout (orders.billing_details). */}
              {order.billing_details && (
                <div className="mt-2 space-y-1 rounded-md border border-border bg-muted/30 p-3">
                  <div className="font-body text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t("orderDetail.legalInfo", "Company requisites")}
                  </div>
                  {order.billing_details.companyName && (
                    <div className="text-foreground">{order.billing_details.companyName}</div>
                  )}
                  {order.billing_details.regNumber && (
                    <div className="text-muted-foreground">
                      {t("orderDetail.regNumber", "Reg. no.")}: {order.billing_details.regNumber}
                    </div>
                  )}
                  {order.billing_details.vatNumber && (
                    <div className="text-muted-foreground">
                      {t("orderDetail.vatNumber", "VAT no.")}: {order.billing_details.vatNumber}
                    </div>
                  )}
                  {order.billing_details.legalAddress && (
                    <div className="text-muted-foreground">
                      {t("orderDetail.legalAddress", "Legal address")}:{" "}
                      {order.billing_details.legalAddress}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Delivery card — mirrors miluliem layout: Method label/value row,
            Address block (formatted multi-line for courier, single line for
            pickup-at-store), Tracking number when present, fulfilment
            badges (chilled/banquet/age-verified) at the bottom. */}
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <MapPin size={16} className="text-muted-foreground" />{" "}
              {t("orderDetail.delivery", "Delivery")}
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  {t("orderDetail.method", "Method")}
                </span>
                <span className="flex items-center gap-1.5 capitalize text-foreground">
                  {order.fulfilment_type === "pickup" ? (
                    <Package size={14} />
                  ) : (
                    <Truck size={14} />
                  )}
                  {order.fulfilment_type === "pickup"
                    ? t("orders.fulfilmentPickup", "Pickup")
                    : t("orders.fulfilmentCourier", "Courier")}
                </span>
              </div>
              {/* Legal 6h rule (spec 2026-09-23 #1): alcohol may not be
                  handed over earlier than 6h after the order, within the
                  allowed sale hours AND the store's opening hours — a raw
                  created_at+6h once showed an after-closing (and illegal)
                  slot (order VG-2026-00050). Ops hint for staff. */}
              {order.fulfilment_type === "pickup" && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t("orderDetail.earliestHandover", "Alkoholu izsniegt ne agrāk kā")}
                  </span>
                  <span className="font-medium text-foreground">
                    {earliestPickupHandoverAt(
                      new Date(order.created_at),
                      typeof order.shipping_address?.pickup === "string"
                        ? order.shipping_address.pickup
                        : (stores.find((s) => s.id === order.fulfilment_store_id)?.name ?? null),
                    ).toLocaleString("lv-LV", {
                      timeZone: "Europe/Riga",
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              )}
              {order.fulfilment_type === "courier" && order.courier_provider && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t("orderDetail.provider", "Provider")}
                  </span>
                  <span className="capitalize text-foreground">
                    {order.courier_provider}
                  </span>
                </div>
              )}
              {/* Earliest legal delivery for courier alcohol orders (LV 6h +
                20:00/17:00-Sun rule). Only set on qualifying orders; do not
                dispatch before this instant. */}
              {order.earliest_delivery_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t("orderDetail.earliestDelivery", "Earliest delivery (alcohol)")}
                  </span>
                  <span className="text-foreground">
                    {new Date(order.earliest_delivery_at).toLocaleString("lv-LV", {
                      timeZone: "Europe/Riga",
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              )}
              {/* Fulfilment-store reassignment. Always shown — pickup
                orders default to the customer's chosen store, courier
                orders to the org default. Admin can switch at any
                time; the next dispatch uses the new store's sender. */}
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">
                  {t("orderDetail.fulfilmentStore", "Fulfilment store")}
                </span>
                <Select
                  value={order.fulfilment_store_id ?? ""}
                  onValueChange={reassignStore}
                  disabled={working || stores.length === 0}
                >
                  <SelectTrigger className="h-8 w-[200px]">
                    <SelectValue
                      placeholder={t("orderDetail.fulfilmentStorePlaceholder", "—")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {order.shipping_address && (
                <div>
                  <div className="mb-1 font-body text-[11px] uppercase tracking-wider text-muted-foreground">
                    {order.fulfilment_type === "pickup"
                      ? t("orderDetail.pickupStore", "Pickup store")
                      : t("orderDetail.address", "Address")}
                  </div>
                  <AddressBlock addr={order.shipping_address} />
                </div>
              )}
              {/* Pakomats orders snapshot the chosen locker on the
                order row so the admin sees exactly what the customer
                picked, even if the upstream Venipak feed later
                drops/renames the row. */}
              {order.pickup_point_terminal && (
                <div>
                  <div className="mb-1 font-body text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t("orderDetail.pakomats", "Parcel locker")}
                  </div>
                  <PakomatsBlock terminal={order.pickup_point_terminal} />
                </div>
              )}
              {order.tracking_number && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t("orderDetail.tracking", "Tracking")}
                  </span>
                  <span className="font-mono text-xs text-foreground">
                    {order.tracking_number}
                  </span>
                </div>
              )}
              {/* Dispatch + label actions — only meaningful when the
                customer chose a courier provider. Already-sent orders
                hide the dispatch button and only expose the label
                download (re-dispatching duplicates the waybill). */}
              {order.fulfilment_type === "courier" &&
                order.courier_provider === "venipak" && (
                  <div className="space-y-2 border-t border-border pt-3">
                    {order.venipak_status === "sent" ? (
                      <>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">
                            {t("orderDetail.venipakStatus", "Venipak")}
                          </span>
                          <Badge variant="success">
                            {t("orderDetail.dispatchedAt", "Dispatched")}
                            {order.venipak_dispatched_at && (
                              <span className="ml-1 font-normal">
                                · {new Date(order.venipak_dispatched_at).toLocaleString("en-GB")}
                              </span>
                            )}
                          </Badge>
                        </div>
                        {order.venipak_pack_numbers && order.venipak_pack_numbers.length > 0 && (
                          <div className="font-mono text-[11px] text-muted-foreground">
                            {order.venipak_pack_numbers.join(", ")}
                          </div>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={downloadLabel}
                        >
                          <FileText size={14} className="mr-1" />
                          {t("orderDetail.downloadLabel", "Download label PDF")}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={dispatchToCourier}
                        disabled={dispatching}
                      >
                        <Truck size={14} className="mr-1" />
                        {dispatching
                          ? t("orderDetail.dispatching", "Dispatching…")
                          : t("orderDetail.dispatch", "Send to Venipak")}
                      </Button>
                    )}
                    {order.venipak_status === "error" && (
                      <p className="text-xs text-destructive">
                        {t(
                          "orderDetail.lastDispatchFailed",
                          "Previous dispatch attempt failed. Retry to try again."
                        )}
                      </p>
                    )}
                  </div>
                )}
              <div className="flex flex-wrap gap-1.5">
                {order.receive_chilled && (
                  <Badge variant="info" className="gap-1">
                    <Snowflake size={10} />
                    {t("orderDetail.chilled", "Chilled")}
                  </Badge>
                )}
                {order.for_banquet && (
                  <Badge variant="outline" className="gap-1">
                    <PartyPopper size={10} />
                    {t("orderDetail.banquet", "For banquet")}
                  </Badge>
                )}
                {order.age_verification_id && (
                  <Badge variant="success" className="gap-1">
                    <ShieldCheck size={10} />
                    {t("orderDetail.ageVerified", "Age verified")}
                  </Badge>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("orderDetail.confirmCancelTitle", "Cancel this order?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "orderDetail.confirmCancelDesc",
                "This marks the order as cancelled. Stock allocation rolls back. Action is reversible by changing status manually."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={cancelOrder}>
              {t("orderDetail.confirmCancelAction", "Yes, cancel order")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={refundOpen} onOpenChange={setRefundOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("orderDetail.confirmRefundTitle", "Mark as refunded?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "orderDetail.confirmRefundDesc",
                "This flips the order + payment status to refunded. Note: this does NOT trigger an actual refund through Montonio — that runs separately when the integration ships."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={refundOrder}>
              {t("orderDetail.confirmRefundAction", "Yes, mark as refunded")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Click-to-zoom product image overlay. Click backdrop or the X to
        dismiss; click the image itself stops propagation so it doesn't
        close while panning. */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex cursor-pointer items-center justify-center bg-black/70"
          onClick={() => setLightboxUrl(null)}
        >
          <div
            className="relative max-h-[80vh] max-w-[80vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxUrl}
              alt=""
              className="max-h-[80vh] max-w-full rounded-lg bg-white object-contain"
            />
            <button
              type="button"
              onClick={() => setLightboxUrl(null)}
              className="absolute -right-3 -top-3 rounded-full border border-border bg-background p-1 transition-colors hover:bg-muted"
              aria-label={t("common.close", "Close")}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function num(n: any): string {
  return Number(n ?? 0).toFixed(2);
}

function AddressBlock({ addr }: { addr: Record<string, unknown> }) {
  // Pickup-at-store orders store only the chosen store label in `pickup`;
  // courier orders have street/city/postal_code/country broken out so we
  // can render a multi-line address that matches the miluliem layout.
  if (addr.pickup) {
    return <div className="text-foreground">{String(addr.pickup)}</div>;
  }
  const street = addr.street as string | undefined;
  const city = addr.city as string | undefined;
  const postalCode = addr.postal_code as string | undefined;
  const region = addr.region as string | undefined;
  const country = addr.country as string | undefined;
  return (
    <div className="space-y-0.5 text-foreground">
      {street && <p>{street}</p>}
      {(postalCode || city) && (
        <p>{[postalCode, city].filter(Boolean).join(", ")}</p>
      )}
      {region && <p>{region}</p>}
      {country && <p>{country}</p>}
    </div>
  );
}

function PakomatsBlock({ terminal }: { terminal: Record<string, unknown> }) {
  const name = String(terminal.name ?? "");
  const address = String(terminal.address ?? "");
  const city = String(terminal.city ?? "");
  const zip = String(terminal.zip ?? "");
  const provider = String(terminal.provider ?? "venipak");
  return (
    <div className="space-y-0.5 text-foreground">
      <p className="font-medium">{name}</p>
      <p className="text-muted-foreground">
        {address}
        {city && `, ${city}`}
        {zip && `, ${zip}`}
      </p>
      <p className="font-body text-[11px] uppercase tracking-wider text-muted-foreground">
        {provider}
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div className={`flex justify-between ${bold ? "border-t border-border pt-2 font-bold" : ""}`}>
      <span className={muted ? "text-muted-foreground" : ""}>{label}</span>
      <span className={muted ? "text-muted-foreground" : ""}>{value}</span>
    </div>
  );
}

function StatusBadge({ status, t }: { status: string; t: (k: string, f: string) => string }) {
  const variant: "default" | "secondary" | "outline" | "success" | "info" | "destructive" =
    status === "paid" || status === "ready"
      ? "info"
      : status === "fulfilled"
        ? "success"
        : status === "cancelled" || status === "refunded"
          ? "destructive"
          : "secondary";
  const label =
    status === "pending"
      ? t("orders.statusPending", "Pending")
      : status === "paid"
        ? t("orders.statusPaid", "Paid")
        : status === "ready"
          ? t("orders.statusReady", "Ready")
          : status === "fulfilled"
            ? t("orders.statusFulfilled", "Fulfilled")
            : status === "cancelled"
              ? t("orders.statusCancelled", "Cancelled")
              : status === "refunded"
                ? t("orders.statusRefunded", "Refunded")
                : status;
  return <Badge variant={variant}>{label}</Badge>;
}

function PaymentBadge({ status, t }: { status: string; t: (k: string, f: string) => string }) {
  const variant: "default" | "secondary" | "outline" | "success" | "info" | "destructive" =
    status === "paid"
      ? "success"
      : status === "failed" || status === "refunded"
        ? "destructive"
        : status === "partially_refunded"
          ? "outline"
          : "secondary";
  return <Badge variant={variant}>{status}</Badge>;
}

const COMM_STATUS_ICON: Record<string, JSX.Element> = {
  queued: <Clock size={12} className="text-muted-foreground" />,
  sent: <Send size={12} className="text-blue-500" />,
  delivered: <CheckCircle2 size={12} className="text-emerald-500" />,
  opened: <Eye size={12} className="text-purple-500" />,
  clicked: <MousePointerClick size={12} className="text-indigo-500" />,
  bounced: <XCircle size={12} className="text-orange-500" />,
  failed: <AlertCircle size={12} className="text-destructive" />,
  unsubscribed: <XCircle size={12} className="text-gray-400" />,
  spam: <AlertCircle size={12} className="text-red-500" />,
};

const COMM_STATUS_COLOR: Record<string, string> = {
  queued: "bg-muted text-muted-foreground",
  sent: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
  opened: "bg-purple-100 text-purple-800",
  clicked: "bg-indigo-100 text-indigo-800",
  bounced: "bg-orange-100 text-orange-800",
  failed: "bg-red-100 text-red-800",
  unsubscribed: "bg-gray-100 text-gray-600",
  spam: "bg-red-100 text-red-900",
};

function CommunicationRow({ comm }: { comm: OrderCommunication }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {comm.channel === "sms" ? (
            <MessageCircle size={13} className="text-muted-foreground" />
          ) : (
            <Mail size={13} className="text-muted-foreground" />
          )}
          <span className="font-body text-xs font-medium capitalize text-foreground">
            {comm.template.replace(/_/g, " ")}
          </span>
          {comm.provider && (
            <span className="font-body text-[10px] text-muted-foreground">
              via {comm.provider}
            </span>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-body text-[10px] font-medium capitalize ${
            COMM_STATUS_COLOR[comm.status] ?? "bg-muted text-muted-foreground"
          }`}
        >
          {COMM_STATUS_ICON[comm.status] ?? null}
          {comm.status}
        </span>
      </div>
      {comm.subject && (
        <p className="font-body text-sm text-foreground">{comm.subject}</p>
      )}
      <p className="font-body text-xs text-muted-foreground">
        To: {comm.recipient} ·{" "}
        {new Date(comm.created_at).toLocaleString("en-GB")}
      </p>
    </div>
  );
}
